/**
 * generateCertificate
 * Draws a PDF-style certificate on an offscreen canvas and triggers a PNG download.
 *
 * @param {object} opts
 * @param {string} opts.userName      - Recipient's display name
 * @param {string} opts.quizTitle     - Quiz title (topic)
 * @param {string} opts.categoryName  - Category name
 * @param {number} opts.score         - Score percentage 0-100
 * @param {string} opts.issuedAt      - ISO date string
 */
export const generateCertificate = ({ userName, quizTitle, categoryName, score, issuedAt }) => {
    const W = 1200;
    const H = 850;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // ── Background ────────────────────────────────────────────────
    // Deep dark gradient
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#0f172a');
    bgGrad.addColorStop(0.5, '#0c1a2e');
    bgGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // ── Decorative circles ────────────────────────────────────────
    const drawCircle = (x, y, r, color) => {
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.restore();
    };
    drawCircle(120, 120, 180, 'rgba(16,185,129,0.07)');
    drawCircle(W - 100, H - 100, 220, 'rgba(16,185,129,0.05)');
    drawCircle(W - 200, 80, 120, 'rgba(99,102,241,0.06)');
    drawCircle(200, H - 80, 140, 'rgba(99,102,241,0.05)');

    // ── Outer border ──────────────────────────────────────────────
    const margin = 32;
    ctx.strokeStyle = 'rgba(16,185,129,0.4)';
    ctx.lineWidth = 2;
    roundRect(ctx, margin, margin, W - margin * 2, H - margin * 2, 20);
    ctx.stroke();

    // Inner border
    ctx.strokeStyle = 'rgba(16,185,129,0.15)';
    ctx.lineWidth = 1;
    roundRect(ctx, margin + 10, margin + 10, W - (margin + 10) * 2, H - (margin + 10) * 2, 15);
    ctx.stroke();

    // ── Corner ornaments ──────────────────────────────────────────
    const corners = [
        [margin + 2, margin + 2],
        [W - margin - 2, margin + 2],
        [margin + 2, H - margin - 2],
        [W - margin - 2, H - margin - 2],
    ];
    corners.forEach(([cx, cy]) => {
        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(16,185,129,0.6)';
        ctx.fill();
    });

    // ── Top badge ─────────────────────────────────────────────────
    ctx.fillStyle = 'rgba(16,185,129,0.15)';
    roundRect(ctx, W / 2 - 110, 65, 220, 36, 18);
    ctx.fill();
    ctx.fillStyle = '#10E58F';
    ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('✦  CERTIFICATE OF ACHIEVEMENT  ✦', W / 2, 88);

    // ── prepxai logo text ─────────────────────────────────────────
    ctx.font = 'bold 26px -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillText('prepxai', W / 2, 145);

    // ── Trophy icon (unicode) ─────────────────────────────────────
    ctx.font = '64px serif';
    ctx.fillText('🏆', W / 2, 240);

    // ── "This certifies that" ─────────────────────────────────────
    ctx.font = '18px -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(148,163,184,0.9)';
    ctx.fillText('This is to certify that', W / 2, 295);

    // ── Name ──────────────────────────────────────────────────────
    ctx.font = 'bold 52px -apple-system, sans-serif';
    ctx.fillStyle = '#f1f5f9';
    ctx.fillText(userName || 'Learner', W / 2, 365);

    // Name underline
    const nameWidth = ctx.measureText(userName || 'Learner').width;
    const lineGrad = ctx.createLinearGradient(W / 2 - nameWidth / 2, 0, W / 2 + nameWidth / 2, 0);
    lineGrad.addColorStop(0, 'transparent');
    lineGrad.addColorStop(0.3, '#10E58F');
    lineGrad.addColorStop(0.7, '#10E58F');
    lineGrad.addColorStop(1, 'transparent');
    ctx.strokeStyle = lineGrad;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - nameWidth / 2, 378);
    ctx.lineTo(W / 2 + nameWidth / 2, 378);
    ctx.stroke();

    // ── Body text ─────────────────────────────────────────────────
    ctx.font = '20px -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(148,163,184,0.9)';
    ctx.fillText('has successfully completed the quiz and demonstrated proficiency in', W / 2, 420);

    // ── Quiz/Category name ────────────────────────────────────────
    ctx.font = 'bold 34px -apple-system, sans-serif';
    ctx.fillStyle = '#10E58F';
    ctx.fillText(quizTitle || categoryName || 'Learning Quiz', W / 2, 475);

    if (categoryName && quizTitle) {
        ctx.font = '18px -apple-system, sans-serif';
        ctx.fillStyle = 'rgba(148,163,184,0.7)';
        ctx.fillText(categoryName, W / 2, 508);
    }

    // ── Score badge ───────────────────────────────────────────────
    ctx.fillStyle = 'rgba(16,185,129,0.12)';
    ctx.strokeStyle = 'rgba(16,185,129,0.4)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, W / 2 - 80, 530, 160, 52, 26);
    ctx.fill();
    ctx.stroke();
    ctx.font = 'bold 28px -apple-system, sans-serif';
    ctx.fillStyle = '#10E58F';
    ctx.fillText(`${score}%`, W / 2, 563);

    // ── Date & divider ────────────────────────────────────────────
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(160, 620);
    ctx.lineTo(W - 160, 620);
    ctx.stroke();

    const dateStr = issuedAt
        ? new Date(issuedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
        : new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });

    ctx.font = '16px -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(148,163,184,0.7)';
    ctx.fillText(`Issued on ${dateStr}  ·  prepxai.in`, W / 2, 660);

    // ── Five stars ────────────────────────────────────────────────
    ctx.font = '24px serif';
    ctx.fillStyle = '#f59e0b';
    ctx.fillText('★ ★ ★ ★ ★', W / 2, 710);

    // ── Watermark shield ─────────────────────────────────────────
    ctx.font = '13px -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(100,116,139,0.5)';
    ctx.fillText('Verified · prepxai Learning Platform', W / 2, 785);

    // ── Trigger download ──────────────────────────────────────────
    const link = document.createElement('a');
    const safeName = (quizTitle || 'certificate').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `prepxai_${safeName}_certificate.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
};

// Utility: draw a rounded rectangle path
function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}
